import { useEffect, useState } from 'react';
import api from '../services/apiClient';
import { useAuth } from './useAuth';

type Listener = (...args: any[]) => void;

class RealtimeConnection {
  private source: EventSource | null = null;
  private listeners = new Map<string, Set<Listener>>();
  private reconnectTimer: number | null = null;
  private retryTimer: number | null = null;
  private closed = false;
  private channelId = '';
  private sequence = 0;
  connected = false;

  on(event: string, listener: Listener) {
    const listeners = this.listeners.get(event) || new Set<Listener>();
    listeners.add(listener);
    this.listeners.set(event, listeners);
    return this;
  }

  off(event: string, listener: Listener) {
    this.listeners.get(event)?.delete(listener);
    return this;
  }

  emit(event: string, payload?: any) {
    if (event === 'runtime:snapshot:request') {
      const requestedChannel = String(payload?.channelId || '');
      if (requestedChannel && requestedChannel !== this.channelId) void this.connect(requestedChannel);
      void api.get('/runtime/snapshot', { params: { channelId: payload?.channelId } })
        .then(({ data }) => this.dispatch('runtime:snapshot', data))
        .catch(() => this.dispatch('runtime:error'));
    }
    return this;
  }

  async connect(channelId = this.channelId) {
    this.closed = false;
    const sequence = ++this.sequence;
    try {
      const { data } = await api.post('/realtime/session', channelId ? { channelId } : {});
      if (this.closed || sequence !== this.sequence) return;
      this.channelId = String(data.selectedChannelId || channelId || '');
      this.source?.close();
      this.source = new EventSource(String(data.url), { withCredentials: true });
      this.source.onopen = () => {
        this.connected = true;
        this.dispatch('connect');
      };
      this.source.onmessage = (message) => {
        try {
          const event = JSON.parse(message.data);
          if (event?.type) this.dispatch(event.type, event.payload);
        } catch {
          this.dispatch('runtime:error');
        }
      };
      this.source.onerror = () => {
        if (this.connected) {
          this.connected = false;
          this.dispatch('disconnect');
        }
      };
      this.scheduleSessionRenewal();
    } catch {
      if (sequence !== this.sequence) return;
      this.connected = false;
      this.dispatch('runtime:error');
      if (!this.closed) {
        if (this.retryTimer) window.clearTimeout(this.retryTimer);
        this.retryTimer = window.setTimeout(() => {
          this.retryTimer = null;
          if (!this.closed) void this.connect();
        }, 3000);
      }
    }
  }

  disconnect() {
    this.closed = true;
    this.sequence += 1;
    this.connected = false;
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    if (this.retryTimer) window.clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.source?.close();
    this.source = null;
    this.listeners.clear();
  }

  private dispatch(event: string, payload?: any) {
    this.listeners.get(event)?.forEach((listener) => listener(payload));
  }

  private scheduleSessionRenewal() {
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = window.setTimeout(() => {
      this.source?.close();
      void this.connect();
    }, 11 * 60 * 60 * 1000);
  }
}

let sharedConnection: RealtimeConnection | null = null;

export const useSocket = () => {
  const { user } = useAuth();
  const [socket, setSocket] = useState<RealtimeConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!user) {
      sharedConnection?.disconnect();
      sharedConnection = null;
      setSocket(null);
      setIsConnected(false);
      return;
    }

    if (!sharedConnection) {
      sharedConnection = new RealtimeConnection();
      void sharedConnection.connect();
    }
    const connection = sharedConnection;
    const handleConnect = () => setIsConnected(true);
    const handleDisconnect = () => setIsConnected(false);
    connection.on('connect', handleConnect);
    connection.on('disconnect', handleDisconnect);
    setSocket(connection);
    setIsConnected(connection.connected);

    return () => {
      connection.off('connect', handleConnect);
      connection.off('disconnect', handleDisconnect);
    };
  }, [user]);

  return { socket, isConnected };
};
